import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { buildClubAdminTour, type TourStep } from "./clubAdminTour";

/**
 * A guided walkthrough that drives the app rather than describing it.
 *
 * Each step navigates to the page it is about, so the reader sees the real
 * screen with the explanation over it — a tour told entirely in a modal
 * teaches the modal, not the product.
 *
 * State lives in localStorage, not on the server. It survives the navigation
 * between steps (which remounts everything), it works per device, which is how
 * learning an interface actually goes, and it needs no round trip to pause or
 * resume. It is a UI preference, not a fact about the club.
 *
 * It is stored PER ACCOUNT, and the card only renders for a signed-in club
 * admin or coordinator. A tour left half-finished used to persist as
 * `active: true` for the whole device, so the next person to open the login
 * page — the same admin after signing out, or anyone else on a shared
 * computer — got a walkthrough of a club they were not in.
 */

const STORAGE_PREFIX = "igrowth_tour_v1";
/** The old device-wide key, cleared on read so it cannot resurrect the tour. */
const LEGACY_KEY = STORAGE_PREFIX;

type Persisted = {
  /** Running right now — kept so a step's navigation does not end the tour. */
  active: boolean;
  index: number;
  /** Set once the tour is finished or dismissed, so it does not reopen itself. */
  seen: boolean;
};

const EMPTY: Persisted = { active: false, index: 0, seen: false };

function read(key: string): Persisted {
  try {
    // The device-wide value from the first version is the bug itself: drop it
    // on sight rather than carrying it into the per-account key.
    localStorage.removeItem(LEGACY_KEY);
    const raw = localStorage.getItem(key);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw);
    return {
      active: !!parsed.active,
      index: Number.isFinite(parsed.index) ? Math.max(0, parsed.index) : 0,
      seen: !!parsed.seen,
    };
  } catch {
    // Private mode, blocked storage, corrupt value — the tour simply starts
    // fresh rather than throwing on app boot.
    return { ...EMPTY };
  }
}

function write(key: string, state: Persisted) {
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    /* the tour still works for this session */
  }
}

type TourApi = {
  active: boolean;
  seen: boolean;
  index: number;
  steps: TourStep[];
  start: () => void;
  stop: () => void;
  next: () => void;
  prev: () => void;
};

const TourContext = createContext<TourApi | null>(null);

/** Always returns an API, so a component can call `start()` without a guard. */
export function useTour(): TourApi {
  const ctx = useContext(TourContext);
  if (ctx) return ctx;
  const noop = () => {};
  return { active: false, seen: true, index: 0, steps: [], start: noop, stop: noop, next: noop, prev: noop };
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { user, token, activeRole, coordinatorsEnabled } = useAuth();

  // Only the two desk roles have these pages at all. Everyone else — signed
  // out, an athlete, a parent opening a shared join link — gets no tour, no
  // matter what is in storage.
  const eligible = !!token && !!user && (activeRole === "club_admin" || activeRole === "coordinator");

  const storageKey = user ? `${STORAGE_PREFIX}:${user.id}` : null;
  const [state, setState] = useState<Persisted>(() => ({ ...EMPTY }));

  // Re-read when the account changes: progress belongs to the person, so
  // signing out and back in as someone else starts from their own state.
  useEffect(() => {
    setState(storageKey ? read(storageKey) : { ...EMPTY });
  }, [storageKey]);

  const steps = useMemo(
    () => buildClubAdminTour({ coordinators: coordinatorsEnabled }),
    [coordinatorsEnabled]
  );

  const persist = useCallback(
    (next: Persisted) => {
      setState(next);
      if (storageKey) write(storageKey, next);
    },
    [storageKey]
  );

  const goTo = useCallback(
    (index: number) => {
      const clamped = Math.min(Math.max(index, 0), steps.length - 1);
      persist({ active: true, index: clamped, seen: state.seen });
      const step = steps[clamped];
      if (step) navigate(step.path);
    },
    [navigate, persist, state.seen, steps]
  );

  const start = useCallback(() => {
    persist({ active: true, index: 0, seen: state.seen });
    if (steps[0]) navigate(steps[0].path);
  }, [navigate, persist, state.seen, steps]);

  const stop = useCallback(() => {
    // Marked seen on ANY exit, including an early one: someone who closed the
    // tour should not be handed it again on the next page load.
    persist({ active: false, index: 0, seen: true });
  }, [persist]);

  const next = useCallback(() => {
    if (state.index >= steps.length - 1) return stop();
    goTo(state.index + 1);
  }, [goTo, state.index, steps.length, stop]);

  const prev = useCallback(() => goTo(state.index - 1), [goTo, state.index]);

  const api = useMemo<TourApi>(
    () => ({
      active: eligible && state.active,
      // Someone who cannot see the tour must not be offered it either: `seen`
      // reads as true so nothing tries to auto-start it.
      seen: eligible ? state.seen : true,
      index: state.index,
      steps,
      start: eligible ? start : () => {},
      stop,
      next,
      prev,
    }),
    [eligible, state.active, state.seen, state.index, steps, start, stop, next, prev]
  );

  return (
    <TourContext.Provider value={api}>
      {children}
      {api.active && <TourOverlay api={api} />}
    </TourContext.Provider>
  );
}

/**
 * The card itself.
 *
 * Deliberately anchored to the bottom of the screen rather than floating over
 * the middle: the point is to read the explanation WHILE seeing the page it
 * describes, and a centred modal hides exactly what it is talking about.
 */
function TourOverlay({ api }: { api: TourApi }) {
  const step = api.steps[api.index];

  // Escape closes it, the same as any other dismissible layer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") api.stop();
      if (e.key === "ArrowRight") api.next();
      if (e.key === "ArrowLeft") api.prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [api]);

  if (!step) return null;

  const isFirst = api.index === 0;
  const isLast = api.index === api.steps.length - 1;
  const pct = Math.round(((api.index + 1) / api.steps.length) * 100);

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] pointer-events-none"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      role="dialog"
      aria-live="polite"
      aria-label="Tur ghidat"
    >
      <div className="max-w-3xl px-4 pb-4 mx-auto pointer-events-auto">
        <div className="overflow-hidden bg-white border shadow-2xl border-slate-200 rounded-2xl">
          <div className="h-1 bg-slate-100">
            <div className="h-full transition-all bg-indigo-500" style={{ width: `${pct}%` }} />
          </div>

          <div className="p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="min-w-0">
                <p className="text-[11px] font-semibold tracking-wider uppercase text-indigo-500">
                  Pasul {api.index + 1} din {api.steps.length}
                </p>
                <h3 className="mt-0.5 text-base font-bold text-slate-800">{step.title}</h3>
              </div>
              <button
                type="button"
                onClick={api.stop}
                className="ml-auto text-2xl leading-none shrink-0 text-slate-300 hover:text-slate-500"
                aria-label="Închide turul"
              >
                ×
              </button>
            </div>

            <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.body}</p>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              <button
                type="button"
                onClick={api.stop}
                className="px-3 py-2 text-sm font-semibold text-slate-500 hover:text-slate-700"
              >
                Închide
              </button>
              <div className="flex gap-2 ml-auto">
                <button
                  type="button"
                  onClick={api.prev}
                  disabled={isFirst}
                  className="px-4 py-2 text-sm font-semibold border rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  Înapoi
                </button>
                <button
                  type="button"
                  onClick={api.next}
                  className="px-4 py-2 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700"
                >
                  {isLast ? "Am terminat" : "Continuă"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
